import type {
  FormField,
  FormTemplateDocument,
  GroupField,
  GroupSelection,
  WidgetField,
} from '../model/form-template-document.types';
import { isActionGroup } from '../model/form-template-document.types';
import { StringUtilities } from '@dermloop/utils';
import { isGroupField, isWidgetField } from '../model/form-field';
import { evaluateFieldCondition } from './evaluate-condition';
import { isUuid } from './is-uuid';
import {
  marker2dCoordinateSchema,
  marker3dCoordinateSchema,
} from './slot-values';
import { getBindingValue } from './submission-bindings';
import type { SubmissionBindings } from './submission-bindings';

export type FieldCompleteness = 'not-started' | 'in-progress' | 'completed';

/** `isSet`: not undefined, not null, not empty string, and (for arrays) not empty. */
function isValueSet(value: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  return Array.isArray(value) ? value.length > 0 : true;
}

function hasText(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    StringUtilities.stripObjectReplacementCharacters(value).trim().length > 0
  );
}

type BindMap = Record<string, string>;

function slotPath(bind: BindMap | undefined, slot: string): string | undefined {
  return bind?.[slot];
}

function slotValue(
  bindings: SubmissionBindings,
  bind: BindMap | undefined,
  slot: string,
): unknown {
  const path = slotPath(bind, slot);
  return path === undefined ? undefined : getBindingValue(bindings, path);
}

/** A field with no `visibleWhen` is visible. */
export function isFieldVisible(
  field: FormField,
  bindings: SubmissionBindings,
): boolean {
  return (
    field.visibleWhen === undefined ||
    evaluateFieldCondition(field.visibleWhen, bindings)
  );
}

/**
 * `required` absent is not required; it is either a static boolean or a
 * condition. A group carries its own rule on the group body, so the same call
 * answers for a widget and for the group that gates on behalf of its children.
 */
export function isFieldRequired(
  field: WidgetField | GroupField,
  bindings: SubmissionBindings,
): boolean {
  const rule = isGroupField(field) ? field.group.required : field.required;
  if (rule === undefined) {
    return false;
  }
  return typeof rule === 'boolean'
    ? rule
    : evaluateFieldCondition(rule, bindings);
}

/**
 * The ids a host has revealed by pressing their fields' add buttons. Absent on
 * the server, which never sees the button state.
 */
export type RevealedFields = ReadonlySet<string>;

/** Nothing revealed yet — a stable identity for a host's initial state. */
export const NO_REVEALED_FIELDS: RevealedFields = new Set<string>();

/**
 * Whether a field is still waiting behind its add button: it declares an
 * `addLabel`, holds no answer, and the host has not revealed it. The rule lives
 * in submit gating and group completeness rather than in visibility — a waiting
 * field stays visible, so every renderer keeps its step number and simply draws
 * the button in place of the control.
 *
 * A host that tracks no reveal state — the server — reads the answer instead:
 * no answer means never added, an answer means shown.
 */
export function isAwaitingAdd(
  field: WidgetField,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): boolean {
  return (
    'addLabel' in field &&
    field.addLabel !== undefined &&
    isFieldVisible(field, bindings) &&
    getWidgetCompleteness(field, bindings) === 'not-started' &&
    revealed?.has(field.id) !== true
  );
}

/**
 * Per-widget completeness contract, evaluated over bound *values* only
 * — never over what was rendered. Refinements that need data the pure
 * engine does not have (resolved ref-data rows) are noted per case below
 * and deferred.
 */
function isWidgetValueComplete(
  field: WidgetField,
  bindings: SubmissionBindings,
): boolean {
  const bind = field.bind;
  switch (field.widget) {
    case 'yes-no-unknown':
    case 'number':
      return isValueSet(slotValue(bindings, bind, 'value'));
    case 'rating':
    case 'scale':
      return isValueSet(slotValue(bindings, bind, 'selected'));
    case 'single-select':
      // "other must be non-empty when the other option is selected" needs
      // the resolved ref-data rows (which row is flagged "other"), which the
      // pure engine does not have. Deferred — the muco launch template avoids
      // the case entirely via the explicit inline-option + sibling-text-field
      // pattern.
      return isValueSet(slotValue(bindings, bind, 'selected'));
    case 'diagnosis-tree':
    case 'diagnosis-search': {
      // Picking a row flagged `requiresUserInput` opens `other` as '', so a
      // held detail is owed text; the rule needs no diagnosis rows.
      const other = slotValue(bindings, bind, 'other');
      return (
        isUuid(slotValue(bindings, bind, 'selected')) &&
        (other === undefined ||
          (typeof other === 'string' && other.trim().length > 0))
      );
    }
    case 'skin-type':
      return isUuid(slotValue(bindings, bind, 'selected'));
    case 'multi-select': {
      // Same allowOther deferral as single-select above.
      const selected = slotValue(bindings, bind, 'selected');
      return Array.isArray(selected) && selected.length > 0;
    }
    case 'text':
    case 'note': {
      const value = slotValue(bindings, bind, 'value');
      return hasText(value);
    }
    case 'image-capture': {
      const images = slotValue(bindings, bind, 'images');
      return Array.isArray(images) && images.length > 0;
    }
    case 'location-picker': {
      // The pin schemas, so completeness matches the submission schema.
      const schema =
        field.params.pickerType === '3d'
          ? marker3dCoordinateSchema
          : marker2dCoordinateSchema;
      return schema.safeParse(slotValue(bindings, bind, 'coordinate')).success;
    }
    case 'clinical-action': {
      // A group is stored while its sub-option is still to be chosen.
      const selected = slotValue(bindings, bind, 'selected');
      return (
        isValueSet(selected) &&
        !field.options.items.some(
          (item) => isActionGroup(item) && item.value === selected,
        )
      );
    }
  }
}

/**
 * Completeness over bound values. A widget with no `bind` is
 * trivially 'completed'; otherwise 'not-started' iff every bound slot is
 * unset, 'completed' iff the widget's own contract is met, else
 * 'in-progress'.
 */
export function getWidgetCompleteness(
  field: WidgetField,
  bindings: SubmissionBindings,
): FieldCompleteness {
  const bind = field.bind;
  if (!bind) {
    return 'completed';
  }
  const anySet = Object.values(bind).some((path) =>
    isValueSet(getBindingValue(bindings, path)),
  );
  if (!anySet) {
    return 'not-started';
  }
  return isWidgetValueComplete(field, bindings) ? 'completed' : 'in-progress';
}

/** The group's children that `visibleWhen` currently keeps on screen. */
export function getVisibleGroupChildren(
  field: GroupField,
  bindings: SubmissionBindings,
): WidgetField[] {
  // Kind-agnostic, so `fields` is the union of the three per-kind child arrays.
  // Widened to one element type because `ts-reset`'s extra
  // `filter(predicate: BooleanConstructor)` overload makes `.filter`
  // unresolvable over that union. This lib's own tsconfigs already leave
  // `@dermloop-types` out, but consumers compile this file from source with the
  // reset in scope, so opting out here does not remove the need.
  const children: readonly WidgetField[] = field.group.fields;
  return children.filter((child) => isFieldVisible(child, bindings));
}

/**
 * How many of a group's children may hold a value. Kind-agnostic — the
 * invariant reads the same over photos as over surfaces — and an unset property
 * is the behaviour that predates it, so the default is read here rather than at
 * each call site.
 */
export function groupSelection(field: GroupField): GroupSelection {
  return field.group.selection ?? 'each';
}

/**
 * Whether the group already holds what its own `required` asks for: one
 * complete child under `one-of`, every visible child under `each`. Only
 * consulted when the group declares `required`; a group that declares none
 * still gates through its children.
 */
function groupRequirementSatisfied(
  field: GroupField,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): boolean {
  const visibleChildren = getVisibleGroupChildren(field, bindings);
  const isComplete = (child: WidgetField) =>
    getWidgetCompleteness(child, bindings) === 'completed';
  // The awaiting-add exemption belongs to `each` only: under `one-of` the group
  // asks for one answer out of its children, and a child still behind its
  // button answers nothing, so it cannot be the one that satisfies it.
  return groupSelection(field) === 'one-of'
    ? visibleChildren.some(isComplete)
    : visibleChildren.every(
        (child) =>
          isAwaitingAdd(child, bindings, revealed) || isComplete(child),
      );
}

/**
 * Whether a group asks the user for anything — what a renderer shows its
 * mandatory marker from. A `one-of` group carries the requirement itself, since
 * a child of one may not declare `required`; every other group still derives it
 * from its visible children, so a document that declares no group `required`
 * marks exactly what it marked before.
 */
export function groupRequiresAnswer(
  field: GroupField,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): boolean {
  return (
    isFieldRequired(field, bindings) ||
    getVisibleGroupChildren(field, bindings).some(
      (child) =>
        isFieldRequired(child, bindings) &&
        !isAwaitingAdd(child, bindings, revealed),
    )
  );
}

/**
 * Group completeness: under `one-of`, holding the one answer the group asks
 * for; otherwise every visible required child complete. A group whose visible
 * children are all untouched reports 'not-started' rather than vacuously
 * 'completed' (e.g. a group with no required children at all).
 */
export function getGroupCompleteness(
  field: GroupField,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): FieldCompleteness {
  const visibleChildren = getVisibleGroupChildren(field, bindings);
  const childStates = visibleChildren.map((child) =>
    getWidgetCompleteness(child, bindings),
  );
  if (childStates.every((state) => state === 'not-started')) {
    return 'not-started';
  }
  if (groupSelection(field) === 'one-of') {
    return childStates.includes('completed') ? 'completed' : 'in-progress';
  }
  const allVisibleRequiredComplete = visibleChildren.every(
    (child, i) =>
      !isFieldRequired(child, bindings) ||
      isAwaitingAdd(child, bindings, revealed) ||
      childStates[i] === 'completed',
  );
  return allVisibleRequiredComplete ? 'completed' : 'in-progress';
}

/** A widget blocks submit when it is visible, required and not yet complete. */
export function fieldGatesSubmit(
  field: WidgetField,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): boolean {
  return (
    isFieldVisible(field, bindings) &&
    isFieldRequired(field, bindings) &&
    !isAwaitingAdd(field, bindings, revealed) &&
    getWidgetCompleteness(field, bindings) !== 'completed'
  );
}

/**
 * A group blocks submit when it is visible, declares `required`, and does not
 * yet hold the answer that requirement asks for. A group with no `required` of
 * its own never blocks: its children do.
 */
export function groupGatesSubmit(
  field: GroupField,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): boolean {
  return (
    isFieldVisible(field, bindings) &&
    isFieldRequired(field, bindings) &&
    !groupRequirementSatisfied(field, bindings, revealed)
  );
}

/**
 * The visible children of a `one-of` group that hold an answer. The invariant
 * is over the bound values — at most one child holds one — so this reads the
 * values themselves rather than any write rule: a child counts once any slot
 * the template bound for it is set, complete or not.
 */
export function getAnsweredOneOfChildren(
  field: GroupField,
  bindings: SubmissionBindings,
): WidgetField[] {
  return getVisibleGroupChildren(field, bindings).filter(
    (child) => getWidgetCompleteness(child, bindings) !== 'not-started',
  );
}

/**
 * A `one-of` group blocks submit when it holds more than one answer, whether or
 * not its `required` is satisfied: the group's own invariant is part of what the
 * document asks for, and submit is gated on the document being adhered to.
 */
export function oneOfGatesSubmit(
  field: GroupField,
  bindings: SubmissionBindings,
): boolean {
  return (
    groupSelection(field) === 'one-of' &&
    getAnsweredOneOfChildren(field, bindings).length > 1
  );
}

/**
 * Everything standing between the form and submit, in document order: visible,
 * required and not yet answered, plus any `one-of` group holding more than one
 * answer. The one walk both the client's submit button and the server's
 * submit-time validation read, so the two can never disagree on what a group
 * asks for.
 *
 * A `one-of` group is reported as itself rather than through its children — no
 * one child is the one that must be answered, so naming a child would point the
 * user at an arbitrary surface. Its children are never walked: `required` on a
 * child of a `one-of` group is rejected at publish time.
 */
export function collectSubmitBlockers(
  document: FormTemplateDocument,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): FormField[] {
  const blockers: FormField[] = [];
  for (const section of document.sections) {
    for (const field of section.fields) {
      if (isGroupField(field)) {
        if (!isFieldVisible(field, bindings)) {
          continue;
        }
        if (
          groupGatesSubmit(field, bindings, revealed) ||
          oneOfGatesSubmit(field, bindings)
        ) {
          blockers.push(field);
        }
        if (groupSelection(field) === 'one-of') {
          continue;
        }
        for (const child of field.group.fields) {
          if (fieldGatesSubmit(child, bindings, revealed)) {
            blockers.push(child);
          }
        }
        continue;
      }
      if (isWidgetField(field)) {
        if (fieldGatesSubmit(field, bindings, revealed)) {
          blockers.push(field);
        }
      }
    }
  }
  return blockers;
}

/**
 * A form can submit iff no visible required field or group is incomplete and no
 * `one-of` group holds more than one answer. A nested widget also needs its
 * group to be visible; hidden fields never gate.
 */
export function canSubmitForm(
  document: FormTemplateDocument,
  bindings: SubmissionBindings,
  revealed?: RevealedFields,
): boolean {
  return collectSubmitBlockers(document, bindings, revealed).length === 0;
}
